import { useContext, useState } from 'react';
import { AssetType, PresignedUrlAction } from '@amzn/fire-tv-campaign-automation-service';
import { KatButton } from '@amzn/katal-react';
import { AssetContext } from 'src/contexts/AssetContext';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { getPresignedUrls } from 'src/utils/clients/controlPlane/apiMethods';
import { AssetStatus } from 'src/types';
import { logger } from 'src/logger';

// Mime types for asset types relevant to embedded mode (Screen Saver / Feature Rotator)
const ASSET_MIME_TYPES: Record<string, string> = {
  [AssetType.BACKGROUND_IMAGE]: 'image/jpeg',
  [AssetType.LOGO]: 'image/png',
  [AssetType.VIDEO_TRAILER]: 'video/mp4',
};

// Restrict postMessage to known Rodeo origin for security
const PARENT_ORIGIN =
  new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get(
    'parentOrigin',
  ) || 'https://advertising.amazon.com';

export const EmbeddedDoneButton = () => {
  const { assetStates } = useContext(AssetContext);
  const client = getControlPlaneClient();
  const [isLoading, setIsLoading] = useState(false);

  const completedAssets = Object.entries(assetStates).filter(
    ([_, state]) => state.key && state.status === AssetStatus.COMPLETED,
  );

  const handleDone = async () => {
    if (!completedAssets.length) return;
    setIsLoading(true);
    try {
      const keys = completedAssets.map(([_, state]) => state.key!);
      const urls = await getPresignedUrls(client, PresignedUrlAction.GET, keys);

      const assets: Record<string, { presignedUrl: string; fileName: string; mimeType: string }> =
        {};
      completedAssets.forEach(([assetType], i) => {
        assets[assetType] = {
          presignedUrl: urls[i].presignedUrl,
          fileName: urls[i].key.split('/').pop() || urls[i].key,
          mimeType: ASSET_MIME_TYPES[assetType] || 'application/octet-stream',
        };
      });

      window.parent.postMessage({ type: 'FRIDA_ASSETS_READY', assets }, PARENT_ORIGIN);
      logger.info('Embedded mode: Assets sent to parent', { assetTypes: Object.keys(assets) });
    } catch (error) {
      logger.error('Embedded mode: Failed to get presigned URLs', { error });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <KatButton
      variant="primary"
      label={isLoading ? 'Preparing assets...' : 'Done'}
      disabled={!completedAssets.length || isLoading}
      onClick={handleDone}
      data-testid="embedded-done-button"
    />
  );
};
